import { createContext, useContext, useState, useEffect } from 'react';
import { authService } from '../services';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is already logged in
    const storedUser = authService.getStoredUser();
    if (storedUser && authService.isAuthenticated()) {
      setUser(storedUser);
    }
    setLoading(false);
  }, []);

  const login = async (email, password) => {
    const data = await authService.login(email, password);
    authService.storeToken(data.access_token);
    
    const userData = {
      user_id: data.user_id,
      email: email,
      role: data.role,
      global_role: data.global_role,
      tenant_id: data.tenant_id,
      auth_provider: 'local',
      must_change_password: data.must_change_password || false
    };
    
    authService.storeUser(userData);
    setUser(userData);
    return data;
  };

  // SSO login with token from redirect
  const loginWithToken = async (token) => {
    // Store the token first
    authService.storeToken(token);
    
    // Fetch user info using the token
    try {
      const userData = await authService.getCurrentUser();
      const user = {
        user_id: userData.user_id,
        email: userData.email,
        full_name: userData.full_name,
        role: userData.role,
        global_role: userData.global_role,
        tenant_id: userData.tenant_id,
        auth_provider: userData.auth_provider || 'entra'
      };
      
      authService.storeUser(user);
      setUser(user);
      return user;
    } catch (error) {
      // If token is invalid, clean up
      authService.logout();
      throw error;
    }
  };

  const logout = () => {
    authService.logout();
    setUser(null);
  };

  // Refresh user data from server
  const refreshUser = async () => {
    if (!authService.isAuthenticated()) return null;
    
    try {
      const userData = await authService.getCurrentUser();
      const user = {
        user_id: userData.user_id,
        email: userData.email,
        full_name: userData.full_name,
        role: userData.role,
        global_role: userData.global_role,
        tenant_id: userData.tenant_id,
        auth_provider: userData.auth_provider
      };
      
      authService.storeUser(user);
      setUser(user);
      return user;
    } catch (error) {
      logout();
      return null;
    }
  };

  const clearMustChangePassword = () => {
    if (user) {
      const updated = { ...user, must_change_password: false };
      authService.storeUser(updated);
      setUser(updated);
    }
  };

  const value = {
    user,
    login,
    loginWithToken,
    logout,
    refreshUser,
    clearMustChangePassword,
    loading,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin'
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
